import React, { useState } from "react";
import styled from "styled-components";

import { useMountAnimation } from "../../utils/use-mount-animation";
import { StaticAssets } from "../styles/custom-assets";
import { theme } from "../styles/theme";
import { RoundedSquare } from "./shape/rounded-square";
import { Spinner } from "./spinner";
import { CancelIcon } from "./svg/cancel-icon";
import { DotsIcon } from "./svg/dots-icon";
import { PauseIcon } from "./svg/pause-icon";
import { PenIcon } from "./svg/pen-icon";
import { PlayIcon } from "./svg/play-icon";

interface MoreButtonProps {
  active: boolean;
  onClick: () => void;
}
const BUTTON_RIGHT_MARGIN = 12;
const BUTTON_SIZE = 34;

export const MoreButton = (props: MoreButtonProps) => {
  const { onClick } = props;
  return (
    <StyledRoundedSquare
      size={BUTTON_SIZE}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <StyledDotsIcon />
    </StyledRoundedSquare>
  );
};

const StyledRoundedSquare = styled(RoundedSquare)`
  background-color: white;
  transition: background-color 0.5s ease;

  &:hover {
    background-color: #f4f4f4;
    cursor: pointer;
  }
`;

const StyledDotsIcon = styled(DotsIcon)`
  fill: black;
  transform: rotate(90deg);
`;

const ActionIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const BlackRoundedSquare = styled(RoundedSquare)<{ $show: boolean }>`
  border: 1px solid ${theme.colors.gray[100]};
  background-color: #ffffff;
  margin-right: ${BUTTON_RIGHT_MARGIN}px;
  transform: ${(props) => (props.$show ? "translateX(0}px)" : `translateX(${BUTTON_SIZE + BUTTON_RIGHT_MARGIN}px)`)};
  opacity: ${(props) => (props.$show ? "1" : "0")};
  transition:
    transform 0.2s,
    opacity 0.2s;
`;

export const DeleteButton = (props: Omit<MenuButtonProps, "children">) => {
  const { onClick, ...other } = props;
  const [loading, setLoading] = useState(false);
  const deleteOperation = async () => {
    try {
      setLoading(true);
      await onClick();
    } finally {
      setLoading(false);
    }
  };
  return (
    <MenuButton onClick={deleteOperation} {...other}>
      {loading ? (
        <Spinner color={theme.colors.blue[800]} size="13px" />
      ) : (
        <ActionIcon src={StaticAssets.trash} alt="" />
      )}
    </MenuButton>
  );
};

export const EditButton = (props: Omit<MenuButtonProps, "children">) => {
  const { onClick, ...other } = props;
  return (
    <MenuButton onClick={onClick} {...other}>
      <PenIcon width={20} fill="#000000" />
    </MenuButton>
  );
};

export const PauseButton = (props: Omit<MenuButtonProps, "children"> & { isPaused?: boolean }) => {
  const { isPaused, onClick, ...other } = props;
  return (
    <MenuButton onClick={onClick} {...other}>
      {isPaused ? <PlayIcon width={20} fill="#000000" /> : <PauseIcon width={20} fill="#000000" />}
    </MenuButton>
  );
};

export const OpenButton = (props: Omit<MenuButtonProps, "children">) => {
  const { onClick, ...other } = props;
  return (
    <MenuButton onClick={onClick} {...other}>
      <ActionIcon src={StaticAssets.eye} alt="" />
    </MenuButton>
  );
};

export const CancelButton = (props: Omit<MenuButtonProps, "children">) => {
  const { onClick, ...other } = props;
  return (
    <MenuButton onClick={onClick} {...other}>
      <CancelIcon width={20} color="#000000" />
    </MenuButton>
  );
};

export const PlayButton = (props: Omit<MenuButtonProps, "children">) => {
  const { onClick, ...other } = props;
  return (
    <MenuButton onClick={onClick} {...other}>
      <PlayIcon width={20} fill="#000000" />
    </MenuButton>
  );
};

interface MenuButtonProps {
  show: boolean;
  onClick: () => void;
  children?: React.ReactNode;
}
const MenuButton = (props: MenuButtonProps) => {
  const { show, onClick, children } = props;
  const [translateAnimation, setTranslateAnimation] = useState(false);
  const { shouldRender, onTransitionEnd } = useMountAnimation(
    show,
    () => setTranslateAnimation(true),
    () => setTranslateAnimation(false),
  );

  return shouldRender ? (
    <BlackRoundedSquare
      size={BUTTON_SIZE}
      $show={translateAnimation}
      onTransitionEnd={onTransitionEnd}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {children}
    </BlackRoundedSquare>
  ) : null;
};
